"use client";
/**
 * @file page.ts
 * @description
 * -----------------------------------------------------
 * Archivo que contiene la página de login
 * -----------------------------------------------------
 * @version 0.0.1a
 * @created 2024-12-19
 * @modified 2026-03-06
 * @author Correcta
 * @author Cristina Bosa
 * @modified Sergio Manjón
 * @copyright © Correcta
 * @license Private
 * */

import InputComponent from "@/components/ui/InputComponent";
import React, { useState } from "react";
import { Login } from "@/types/authentication";
import { LoginSchema } from "@/schemas/AuthenticationSchemas";
import { getSession, signIn } from "next-auth/react";
import { useRouter } from "next/navigation";
import { useTranslations } from "use-intl";
import { Link } from "@/i18n/routing";
import { UserRoles } from "@/enums/userRoles.enum";
import FormComponent from "@/components//ui/FormComponent";
import {
  acceptCookies,
  hasAcceptedCookies,
  savePreferenceCookies,
} from "@/lib/cookieUtils";
import { HTTPStatus } from "@/constants/HttpStatus";
import { ResponseMessages } from "@/enums/response-messages.enum";

const LoginForm = () => {
  const router = useRouter();
  const labels = useTranslations("Labels");
  const placeholders = useTranslations("Placeholders");
  const login = useTranslations("Login");

  const [errors, setErrors] = useState<{ [key: string]: string[] } | undefined>(
    {},
  );
  const [formData, setFormData] = useState<Login>({
    mail: "",
    password: "",
  });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = event.target;

    setFormData({
      ...formData,
      [id]: value,
    });

    if (errors && errors[id]) {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors[id];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (formData: FormData) => {
    const data: Record<string, string> = Object.fromEntries(
      formData.entries(),
    ) as Record<string, string>;

    const validationResult = LoginSchema().safeParse(data);

    if (validationResult.success) {
      setErrors({});
      const response = await signIn("credentials", {
        mail: data.mail,
        password: data.password,
        redirect: false,
      });

      if (response?.status === HTTPStatus.UNAUTHORIZED) {
        const unauthorizedMsg = ResponseMessages.INVALID_CREDENTIALS;
        setErrors({
          general: [unauthorizedMsg],
          mail: [unauthorizedMsg],
          password: [unauthorizedMsg],
        });
      }

      if (!response?.error) {
        const session = await getSession();
        const language = session?.user.language;
        const userRole = session?.user.role;
        const hasCookies = await hasAcceptedCookies();
        if (!session) return;

        if (!hasCookies) {
          await acceptCookies();
          await savePreferenceCookies(true);
        }

        const target =
          session?.requiresOtp && !session?.verifiedOtp
            ? "two-factor-code"
            : userRole === UserRoles.SUPERADMIN
              ? "users-management"
              : "campaigns";

        router.push(`/${language}/${target}`);
      }
    } else {
      const formattedErrors = validationResult.error.flatten().fieldErrors;
      setErrors(formattedErrors);
    }
  };
  return (
    <>
      <section className="auth__login">
        <h1 className="auth__title">{login("title")}</h1>
        <FormComponent
          formClassName={"form"}
          formAction={handleSubmit}
          formData={formData}
          buttonSubmit={true}
          buttonClassName={"btn btn--full"}
          buttonSubmitText={"buttonLogin"}
          useNativeForm={true}
        >
          <InputComponent
            id="mail"
            name="mail"
            label={labels("labelMailRequired")}
            type="email"
            placeholder={placeholders("placeholderMail")}
            value={formData.mail}
            onChange={handleChange}
            error={errors?.mail && errors.mail[0]}
            autoComplete="email"
          />
          <InputComponent
            id="password"
            name="password"
            label={labels("labelPasswordRequired")}
            type="password"
            placeholder={placeholders("placeholderPassword")}
            value={formData.password}
            onChange={handleChange}
            error={errors?.password && errors.password[0]}
            autoComplete="current-password"
          />
          <section className="flex__column">
            <p className="auth__login--required">{labels("labelRequired")}</p>
            <Link href={"/send-reset-email"} className={"auth__link"}>
              <p className={`auth__changePassword`}>
                {login("link-forgot-password")}
              </p>
            </Link>
          </section>
        </FormComponent>
      </section>
    </>
  );
};

export default LoginForm;
